import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';
import type { JSX } from 'react';

import { OnboardingProgress } from './OnboardingProgress';
import panelStyles from './TenantLifecyclePanel.module.scss';
import styles from './TenantOnboardingPanel.module.scss';
import { getTenantOnboarding } from '../api/tenants.graphql';
import { ONBOARDING_STEPS, type TenantTab } from '../tenant-labels';

/**
 * Bajo `platform-tenants`: cualquier cambio que invalide el listado (datos, branding, módulos,
 * estado) vuelve a calcular el checklist.
 */
export function tenantOnboardingQueryKey(tenantId: string): readonly unknown[] {
  return ['platform-tenants', 'onboarding', tenantId];
}

interface Props {
  tenantId: string;
  /** Abre la pestaña de la ficha donde se completa el paso. */
  onGoToTab: (tab: TenantTab) => void;
}

/**
 * OS-1718 — checklist de activación del tenant: progreso y "Pendiente por configurar" con enlace
 * directo a cada paso que se completa desde la consola. Se calcula en el API con los datos reales
 * del tenant: no hay nada que marcar a mano.
 */
export function TenantOnboardingPanel({ tenantId, onGoToTab }: Props): JSX.Element {
  const query = useQuery({
    queryKey: tenantOnboardingQueryKey(tenantId),
    queryFn: () => getTenantOnboarding(tenantId),
  });
  const data = query.data;
  const pending = data?.steps.filter((step) => !step.done) ?? [];
  const done = data?.steps.filter((step) => step.done) ?? [];

  function goTo(key: keyof typeof ONBOARDING_STEPS): void {
    const target = ONBOARDING_STEPS[key].target;
    if (!target) {
      return;
    }
    if ('tab' in target) {
      onGoToTab(target.tab);
      return;
    }
    const element = document.getElementById(target.anchor);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    element?.focus({ preventScroll: true });
  }

  return (
    <section className={panelStyles.panel} aria-labelledby="tenant-onboarding-title">
      <div className={panelStyles.head}>
        <h2 id="tenant-onboarding-title" className={panelStyles.title}>
          Activación del tenant
        </h2>
        {data ? <OnboardingProgress onboarding={data} label="Progreso de activación" /> : null}
      </div>

      {query.isLoading ? <p className={panelStyles.reason} role="status">Cargando…</p> : null}
      {query.isError ? (
        <p className={panelStyles.reason} role="alert">
          No se pudo cargar el progreso: {query.error.message}
        </p>
      ) : null}
      {data?.complete ? (
        <p className={panelStyles.reason}>Todos los pasos de activación están completos.</p>
      ) : null}

      {pending.length > 0 ? (
        <>
          <h3 className={styles.subtitle}>Pendiente por configurar</h3>
          <ul className={styles.steps} aria-label="Pendiente por configurar">
            {pending.map((step) => {
              const view = ONBOARDING_STEPS[step.key];
              return (
                <li key={step.key} className={styles.step}>
                  <div className={styles.stepText}>
                    <span className={styles.stepLabel}>
                      <Circle size={14} className={styles.pendingIcon} aria-hidden="true" />
                      {view.label}
                    </span>
                    <span className={styles.stepHint}>{view.hint}</span>
                  </div>
                  {view.target ? (
                    <button
                      type="button"
                      className={panelStyles.actionBtn}
                      onClick={() => goTo(step.key)}
                    >
                      Ir a configurar
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {done.length > 0 && pending.length > 0 ? (
        <ul className={styles.doneList} aria-label="Pasos completados">
          {done.map((step) => (
            <li key={step.key}>
              <CheckCircle2 size={14} className={styles.doneIcon} aria-hidden="true" />
              {ONBOARDING_STEPS[step.key].label}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
